/**
 * "Ver execução" (D13): o andamento do terraform, aberto pela pessoa, só
 * durante a execução e nos 10 min depois (`disponivel` vem do servidor —
 * `pedido.andamento_disponivel`, `leitor_do_andamento.andamento_disponivel`).
 *
 * Texto na fonte do tema (Barlow, `src/theme/index.ts` — sem sobrescrever:
 * a Arial não entra aqui, só na cadeia de fallback do PRÓPRIO tema); só as
 * LINHAS de log (dado bruto do terraform) vão em JetBrains Mono (`Dado`,
 * mesmo token de `TerraformAdjustCard`). Alinhado à esquerda (padrão do
 * design system, `docs/DESIGN_SYSTEM.md`), sem ícone-emoji — `IconTerminal2`
 * é SVG.
 *
 * Rodada 1 (I1): "execução · atualizado há Ns" é FRASE (legenda em
 * português), não dado — `docs/DESIGN_SYSTEM.md` §3.2/§3.4 reserva
 * JetBrains Mono para protocolo/código/valor numérico. Saiu do `<Dado>` e
 * foi para `<Text>`, igual às outras legendas deste componente.
 */
import { useEffect, useState } from 'react'
import { Button, Collapse, ScrollArea, Stack, Text } from '@mantine/core'
import { IconTerminal2 } from '@tabler/icons-react'
import { useAndamentoDoPedido } from '@/hooks/useAndamentoDoPedido'
import { useSeguirOFim } from '@/hooks/useSeguirOFim'
import { AcompanhamentoPorRecurso } from './AcompanhamentoPorRecurso'
import { Dado } from './Dado'
import { recursosDoApply } from './recursosDoApply'

function useSegundosDesde(momento: Date | null): number | null {
  const [agora, setAgora] = useState(() => Date.now())
  useEffect(() => {
    if (!momento) return
    const t = setInterval(() => setAgora(Date.now()), 1000)
    return () => clearInterval(t)
  }, [momento])
  return momento ? Math.max(0, Math.round((agora - momento.getTime()) / 1000)) : null
}

// "N de M" (TASK-12155): o M é a soma do `Plan:` aprovado (o import não muda
// recurso), o N são as linhas de mudança concluída que o painel já leu. `Read
// complete` é data source e não conta.
const _PLAN = /Plan: (?:\d+ to import, )?(\d+) to add, (\d+) to change, (\d+) to destroy/
const _CONCLUIDA = /: (?:Creation|Modifications|Destruction) complete after /

export function totalDoPlan(linhaPlan: string | null | undefined): number {
  const m = linhaPlan ? _PLAN.exec(linhaPlan) : null
  return m ? Number(m[1]) + Number(m[2]) + Number(m[3]) : 0
}

export function contarMudancas(linhas: string[]): number {
  return linhas.filter((l) => _CONCLUIDA.test(l)).length
}

export interface PainelDeExecucaoProps {
  pedidoId: number
  disponivel: boolean
  /** O chamado de onde o painel foi aberto (TASK-12155): a rota aceita ler o
   * chamado no lugar da conversa. */
  viaChamado?: number | null
  /** Abre sem clique: no chamado, durante o apply, quem abriu está olhando. */
  abertoDeInicio?: boolean
  /** O M do "N de M mudanças feitas" (`totalDoPlan`); 0 esconde a frase. */
  totalDeMudancas?: number
  /** O plano por endereço (`pedido.acoes`). Com ele, o apply vira lista por
   * recurso (spec 2026-10-01-acompanhamento-do-apply-por-recurso). */
  acoes?: Record<string, string[]>
}

export function PainelDeExecucao({
  pedidoId, disponivel, viaChamado = null, abertoDeInicio = false, totalDeMudancas = 0, acoes,
}: PainelDeExecucaoProps) {
  const [aberto, setAberto] = useState(abertoDeInicio)
  const [logAberto, setLogAberto] = useState(false)
  // O pedido muda de estado com a página aberta (replan -> apply): abre sozinho
  // quando passa a valer, e nunca fecha o que a pessoa abriu.
  useEffect(() => {
    if (abertoDeInicio) setAberto(true)
  }, [abertoDeInicio])
  const { linhas, execucao, situacao, lidoEm, encerrado, leituraRecusada } =
    useAndamentoDoPedido(pedidoId, aberto && disponivel, viaChamado)
  const segundos = useSegundosDesde(lidoEm)
  // Linha nova rola o log para o fim, como num terminal (consoleai#25060).
  const seguirOFim = useSeguirOFim<HTMLDivElement>(linhas)
  const feitas = contarMudancas(linhas)
  // O replan roda na família do apply e não conclui mudança: só o apply (ou
  // o que já concluiu algo) mostra o contador.
  const mostraContador = totalDeMudancas > 0 && (execucao === 'apply' || feitas > 0)
  const porRecurso = mostraContador && acoes && Object.keys(acoes).length > 0
    ? recursosDoApply(acoes, linhas) : null

  if (!disponivel) return null

  return (
    <Stack gap={4} mt="xs" align="flex-start">
      <Button
        size="xs"
        variant="subtle"
        color="inovareTeal"
        leftSection={<IconTerminal2 size={16} />}
        onClick={() => setAberto((a) => !a)}
      >
        {aberto ? 'Esconder execução' : 'Ver execução'}
      </Button>
      <Collapse in={aberto} style={{ width: '100%' }}>
        <Stack gap={2} align="flex-start">
          <Text size="xs" c="dimmed">
            {execucao ?? 'execução'}
            {segundos != null && ` · atualizado há ${segundos}s`}
          </Text>
          {porRecurso && (
            <AcompanhamentoPorRecurso recursos={porRecurso.recursos} erroSolto={porRecurso.erroSolto}
                                      feitas={feitas} total={totalDeMudancas} />
          )}
          {mostraContador && !porRecurso && (
            <Text size="sm">{feitas} de {totalDeMudancas} mudanças feitas</Text>
          )}
          {/* M1 (revisão final): com `encerrado` mas SEM linha nenhuma lida
              (janela fechou antes da pessoa abrir o painel, ou 401/403/404 —
              ver `useAndamentoDoPedido`), "as linhas acima" mentia — não
              havia nenhuma acima. */}
          {leituraRecusada && (
            <Text size="xs" c="dimmed">
              não foi possível ler o andamento (sessão ou acesso) — recarregue a página
              {linhas.length > 0 && '; as linhas acima são as últimas lidas'}
            </Text>
          )}
          {encerrado && !leituraRecusada && linhas.length > 0 && (
            <Text size="xs" c="dimmed">execução encerrada — as linhas acima são as últimas lidas</Text>
          )}
          {encerrado && !leituraRecusada && linhas.length === 0 && (
            <Text size="xs" c="dimmed">a execução já terminou — o andamento não está mais disponível</Text>
          )}
          {situacao === 'atrasado' && (
            <Text size="xs" c="dimmed">atualização atrasada — tentando de novo</Text>
          )}
          {situacao === 'indisponivel' && (
            <Text size="xs" c="dimmed">
              andamento indisponível agora — o pedido segue normalmente
            </Text>
          )}
          {situacao === 'iniciando' && linhas.length === 0 && (
            <Text size="sm">iniciando a execução…</Text>
          )}
          {porRecurso && (
            <Button size="xs" variant="subtle" color="inovareTeal"
                    onClick={() => setLogAberto((a) => !a)}>
              {logAberto ? 'Esconder log' : 'Ver log'}
            </Button>
          )}
          {(!porRecurso || logAberto) && (
            <ScrollArea.Autosize mah={220} viewportRef={seguirOFim} style={{ width: '100%' }}>
              <Stack gap={0} align="flex-start">
                {linhas.map((linha, i) => (
                  <div key={`${i}-${linha}`}>
                    <Dado>{linha}</Dado>
                  </div>
                ))}
              </Stack>
            </ScrollArea.Autosize>
          )}
        </Stack>
      </Collapse>
    </Stack>
  )
}
